import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Link as RouterLink } from "react-router-dom";
import { auth, db } from "../firebase";
import Atras from "./atras";
import BottomNav from "./BottomNav";
import { normalize } from "../placeSearch";

export const SUPPORT_EMAIL = "support@dapurtecnologia.com";
// FormSubmit reenvía el formulario por email sin necesidad de backend
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${SUPPORT_EMAIL}`;
// El mismo buzón recibe los formularios de varias apps; esto identifica de cuál viene.
const APP_NAME = "Wandr";

const faqs = [
  {
    category: "Buscar y contratar",
    question: "¿Cómo busco alojamiento?",
    answer:
      "En «Inicio» escribí el nombre de un alojamiento, una dirección o una ciudad y elegí una sugerencia: el mapa te lleva ahí. También podés elegir un continente, o mover el mapa y tocar «Buscar en esta zona».",
  },
  {
    category: "Buscar y contratar",
    question: "¿Qué diferencia hay entre los pines verdes y los azules?",
    answer:
      "Los verdes son alojamientos publicados en Wandr: podés chatear con el anfitrión y contratar desde la app. Los azules son hoteles de Google Maps que todavía no están en Wandr y te llevan a su ficha en Google.",
  },
  {
    category: "Buscar y contratar",
    question: "¿Cómo contrato un alojamiento?",
    answer:
      "Abrí la publicación y tocá «Consultar» para chatear con el anfitrión y coordinar los detalles. Desde el chat podés pagar con PayPal; cuando el pago se aprueba, el alojamiento aparece en «Viajes».",
  },
  {
    category: "Publicaciones",
    question: "¿Cómo publico mi alojamiento?",
    answer:
      "Entrá en «Publicar», completá título, descripción y precio por día, elegí país, provincia, localidad y dirección, subí las fotos y guardá. La publicación aparece en el mapa al instante.",
  },
  {
    category: "Publicaciones",
    question: "¿Puedo editar o eliminar una publicación?",
    answer:
      "Sí. En «Perfil» → «Mis propiedades» podés editar o eliminar cada una. Solo vos podés modificar tus publicaciones.",
  },
  {
    category: "Publicaciones",
    question: "Mi hotel ya aparece en Google, ¿cómo lo reclamo?",
    answer:
      "En el mapa tocá el pin azul de tu hotel y elegí «¿Es tu alojamiento? Publicalo en Wandr». Para verificar que sos el dueño te mandamos un código a un email del dominio del sitio web del hotel, o por SMS o llamada al teléfono que figura en Google. Una vez verificado, tu publicación muestra la insignia ✓.",
  },
  {
    category: "Publicaciones",
    question: "¿Por qué mi publicación no aparece en el mapa?",
    answer:
      "La ubicamos a partir de la dirección. Si está incompleta o mal escrita puede que no la encontremos: revisala en «Mis propiedades». Si sigue sin aparecer, escribinos.",
  },
  {
    category: "Seguridad",
    question: "¿Es seguro pagar por Wandr?",
    answer:
      "Los pagos se procesan con PayPal; Wandr nunca ve los datos de tu tarjeta. No pagues por fuera de la app ni compartas datos de pago por chat.",
  },
  {
    category: "Seguridad",
    question: "¿Cómo denuncio una publicación sospechosa?",
    answer:
      "Escribinos con el formulario de abajo eligiendo el motivo «Denunciar una publicación» e incluí el título del alojamiento. Lo revisamos a la brevedad.",
  },
  {
    category: "Cuenta",
    question: "Olvidé mi contraseña, ¿qué hago?",
    answer:
      "En la pantalla de inicio de sesión tocá «¿Olvidaste tu contraseña?» y te enviamos un enlace para crear una nueva.",
  },
  {
    category: "Cuenta",
    question: "¿Cómo cambio mis datos o mi foto de perfil?",
    answer:
      "En «Perfil» tocá el ícono de la cámara para cambiar tu foto, o entrá en «Editar perfil» para actualizar tus datos.",
  },
];

const reasons = [
  "Consulta general",
  "Problema con mi cuenta",
  "Problema con una publicación",
  "Problema con un pago",
  "Verificar mi hotel",
  "Denunciar una publicación",
  "Sugerencia",
  "Otro",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// honey: campo trampa invisible; si viene completo es un bot y FormSubmit descarta el envío
const emptyForm = { name: "", email: "", reason: reasons[0], subject: "", message: "", honey: "" };

const Section = ({ title, description, children }) => (
  <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>
    <Typography variant="h6">{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {description}
      </Typography>
    )}
    {children}
  </Paper>
);

const Support = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  // Precargar nombre y email del usuario logueado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return;
      let name = currentUser.displayName || "";
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) name = userDoc.data().displayName || name;
      } catch {
        // Error ignorado intencionalmente
      }
      setForm((prev) => ({
        ...prev,
        name: prev.name || name,
        email: prev.email || currentUser.email || "",
      }));
    });
    return () => unsubscribe();
  }, []);

  const filteredFaqs = useMemo(() => {
    const words = normalize(search).split(/\s+/).filter(Boolean);
    if (!words.length) return faqs;
    return faqs.filter((faq) => {
      const text = normalize(`${faq.category} ${faq.question} ${faq.answer}`);
      return words.every((word) => text.includes(word));
    });
  }, [search]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Ingresá tu nombre.";
    if (!EMAIL_PATTERN.test(form.email.trim())) next.email = "Ingresá un email válido.";
    if (!form.subject.trim()) next.subject = "Ingresá un asunto.";
    if (form.message.trim().length < 10) next.message = "Contanos un poco más (mínimo 10 caracteres).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSending(true);
    setStatus(null);

    const name = form.name.trim();
    const email = form.email.trim();
    const subject = `[${APP_NAME} · ${form.reason}] ${form.subject.trim()}`;
    const message = form.message.trim();

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: subject,
          _replyto: email,
          _template: "table",
          _captcha: "false",
          _honey: form.honey,
          App: APP_NAME,
          Nombre: name,
          Email: email,
          Motivo: form.reason,
          Asunto: form.subject.trim(),
          Mensaje: message,
          Página: window.location.href,
        }),
      });
      const result = await response.json().catch(() => ({}));
      // El primer envío desde un sitio nuevo queda pendiente hasta confirmar el email de activación de FormSubmit
      if (/activat/i.test(result.message || "")) {
        setStatus({ severity: "info", message: "El formulario se está activando. Probá de nuevo en unos minutos." });
        return;
      }
      if (!response.ok || String(result.success) !== "true") {
        throw new Error(result.message || `FormSubmit respondió ${response.status}`);
      }
      setStatus({ severity: "success", message: "¡Gracias! Recibimos tu mensaje y te vamos a responder por email." });
      setForm((prev) => ({ ...emptyForm, name: prev.name, email: prev.email }));
    } catch (error) {
      setStatus({
        severity: "error",
        message: `No pudimos enviar tu mensaje. Probá de nuevo o escribinos a ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Container>
      <AppBar position="fixed" sx={{ bgcolor: "#0d2219" }}>
        <Toolbar sx={{ gap: 2, py: 1 }}>
          <Atras />
          <Box>
            <Typography variant="h6" color="white" sx={{ lineHeight: 1.2 }}>
              Soporte
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Preguntas frecuentes y contacto
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={{ mt: 12, mb: { xs: 14, md: 6 } }}>
        <Stack spacing={3}>
          {/* Preguntas frecuentes */}
          <Section title="Preguntas frecuentes" description="Las respuestas a las consultas más comunes.">
            <TextField
              placeholder="Buscar en las preguntas..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {filteredFaqs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No encontramos preguntas con ese texto. Escribinos con el formulario de abajo.
              </Typography>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                {filteredFaqs.map((faq, index) => (
                  <Accordion
                    key={faq.question}
                    disableGutters
                    elevation={0}
                    square
                    expanded={expanded === faq.question}
                    onChange={(_, isOpen) => setExpanded(isOpen ? faq.question : false)}
                    sx={{
                      border: 0,
                      borderTop: index > 0 ? 1 : 0,
                      borderColor: "divider",
                      "&::before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "primary.light", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}
                        >
                          {faq.category}
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>{faq.question}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Section>

          {/* Formulario de contacto */}
          <Section
            title="¿No encontraste lo que buscabas?"
            description="Escribinos y te respondemos por email lo antes posible."
          >
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Box
                component="input"
                type="text"
                name="honey"
                value={form.honey}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField select label="Motivo" name="reason" value={form.reason} onChange={handleChange} fullWidth>
                    {reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Asunto"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    inputProps={{ maxLength: 120 }}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mensaje"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message || `${form.message.length}/2000`}
                    inputProps={{ maxLength: 2000 }}
                    required
                    multiline
                    rows={6}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {status && (
                <Alert severity={status.severity} sx={{ mt: 3 }}>
                  {status.message}
                </Alert>
              )}

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                sx={{ mt: 3 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
                  <MailOutlineIcon fontSize="small" />
                  <Typography variant="body2">
                    También podés escribir a{" "}
                    <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: "primary.light", fontWeight: 600 }}>
                      {SUPPORT_EMAIL}
                    </Link>
                  </Typography>
                </Stack>
                <Button type="submit" variant="contained" size="large" endIcon={<SendIcon />} disabled={sending}>
                  {sending ? "Enviando..." : "Enviar mensaje"}
                </Button>
              </Stack>
            </Box>
          </Section>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Consultá también los{" "}
            <Link component={RouterLink} to="/terms" sx={{ color: "inherit" }}>
              Términos de servicio
            </Link>{" "}
            y la{" "}
            <Link component={RouterLink} to="/policy" sx={{ color: "inherit" }}>
              Política de privacidad
            </Link>
            .
          </Typography>
        </Stack>
      </Container>
      <BottomNav />
    </Container>
  );
};

export default Support;
